import React from "react";
import {
    subIndustryOptionsByIndustry,
    industryOptions,
    goalOptions,
    toneOptions,
    ctaOptions,
    brandStyleOptions,
    pricePositioningOptions
} from "../../data/websiteOptions";

function Field({ label, children, full = false }) {
    return (
        <div className={`wg-field ${full ? "wg-field-full" : ""}`}>
            <label className="wg-label">{label}</label>
            {children}
        </div>
    );
}

function TwoCol({ children }) {
    return <div className="wg-grid-2">{children}</div>;
}

export default function BusinessDetailsStep({
    form,
    updateField,
    onAutofill,
    autofilling,
    mode,
    setMode,
    showOfferings,
    setShowOfferings,
    showAdvanced,
    setShowAdvanced
}) {
    const offeredItems = Array.isArray(form.offeredItems) ? form.offeredItems : [];

    const updateOfferedItem = (index, key, value) => {
        const next = [...offeredItems];
        next[index] = {
            ...next[index],
            [key]: value
        };
        updateField("offeredItems", next);
    };

    const addOfferedItem = () => {
        updateField("offeredItems", [
            ...offeredItems,
            {
                id: crypto.randomUUID(),
                name: "",
                subtitle: "",
                description: "",
                badge: "",
                category: "",
                audience: "",
                level: "",
                imagePreference: "",
                keyPoints: [""]
            }
        ]);
    };

    const removeOfferedItem = (index) => {
        const next = offeredItems.filter((_, i) => i !== index);
        updateField(
            "offeredItems",
            next.length > 0
                ? next
                : [
                    {
                        id: crypto.randomUUID(),
                        name: "",
                        subtitle: "",
                        description: "",
                        badge: "",
                        category: "",
                        audience: "",
                        level: "",
                        imagePreference: "",
                        keyPoints: [""]
                    }
                ]
        );
    };

    const addKeyPoint = (itemIndex) => {
        const next = [...offeredItems];
        next[itemIndex] = {
            ...next[itemIndex],
            keyPoints: [...(next[itemIndex].keyPoints || []), ""]
        };
        updateField("offeredItems", next);
    };

    const updateKeyPoint = (itemIndex, pointIndex, value) => {
        const next = [...offeredItems];
        const points = [...(next[itemIndex].keyPoints || [])];
        points[pointIndex] = value;
        next[itemIndex] = {
            ...next[itemIndex],
            keyPoints: points
        };
        updateField("offeredItems", next);
    };

    const removeKeyPoint = (itemIndex, pointIndex) => {
        const next = [...offeredItems];
        const points = [...(next[itemIndex].keyPoints || [])].filter((_, i) => i !== pointIndex);
        next[itemIndex] = {
            ...next[itemIndex],
            keyPoints: points.length > 0 ? points : [""]
        };
        updateField("offeredItems", next);
    };

    return (
        <div className="wg-step-shell">
            <div className="wg-mode-bar">
                <div>
                    <h2 className="wg-section-title">Business Details</h2>
                    <p className="wg-section-subtitle">
                        Start with the basics. AI will help shape content, offerings, pages, and
                        visual direction based on your business type.
                    </p>
                </div>

                <div className="wg-mode-toggle">
                    <button
                        type="button"
                        className={`wg-chip-btn ${mode === "fast" ? "active" : ""}`}
                        onClick={() => setMode("fast")}
                    >
                        Fast Mode
                    </button>
                    <button
                        type="button"
                        className={`wg-chip-btn ${mode === "detailed" ? "active" : ""}`}
                        onClick={() => setMode("detailed")}
                    >
                        Detailed Mode
                    </button>
                </div>
            </div>

            <div className="wg-ai-banner">
                <div>
                    <strong>AI Autofill</strong>
                    <p>
                        Give the essentials first. AI will suggest audience, pages, tone,
                        offerings, and structure.
                    </p>
                </div>

                <button
                    type="button"
                    className="wg-primary-btn"
                    onClick={onAutofill}
                    disabled={autofilling}
                >
                    {autofilling ? "Autofilling..." : "Use AI to Fill the Rest"}
                </button>
            </div>

            <div className="wg-block">
                <div className="wg-block-header">
                    <div>
                        <h3 className="wg-block-title">Essentials</h3>
                        <p className="wg-block-subtitle">
                            These are the main inputs used to define your website direction.
                        </p>
                    </div>
                </div>

                <TwoCol>
                    <Field label="Business Name">
                        <input
                            type="text"
                            value={form.businessName}
                            onChange={(e) => updateField("businessName", e.target.value)}
                            placeholder="e.g. Shriansh AI"
                        />
                    </Field>

                    <Field label="Industry">
                        <select
                            value={form.industry}
                            onChange={(e) => {
                                const nextIndustry = e.target.value;
                                updateField("industry", nextIndustry);

                                const nextSuggestions = subIndustryOptionsByIndustry[nextIndustry] || [];
                                if (
                                    nextSuggestions.length > 0 &&
                                    !nextSuggestions.includes(form.subIndustry)
                                ) {
                                    updateField("subIndustry", "");
                                }
                            }}
                        >
                            <option value="">Select Industry</option>
                            {industryOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </Field>
                </TwoCol>

                <TwoCol>
                    <Field label="Sub Industry">
                        {subIndustryOptionsByIndustry[form.industry]?.length > 0 ? (
                            <div className="wg-stack-sm">
                                <select
                                    value={form.subIndustry}
                                    onChange={(e) => updateField("subIndustry", e.target.value)}
                                >
                                    <option value="">Select Sub Industry</option>
                                    {subIndustryOptionsByIndustry[form.industry].map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>

                                <input
                                    type="text"
                                    value={form.subIndustry}
                                    onChange={(e) => updateField("subIndustry", e.target.value)}
                                    placeholder="Or type a custom sub industry"
                                />
                            </div>
                        ) : (
                            <input
                                type="text"
                                value={form.subIndustry}
                                onChange={(e) => updateField("subIndustry", e.target.value)}
                                placeholder="e.g. AI Automation, CRM, Web Apps"
                            />
                        )}
                    </Field>

                    <Field label="Goal of Website">
                        <select
                            value={form.goalOfWebsite}
                            onChange={(e) => updateField("goalOfWebsite", e.target.value)}
                        >
                            <option value="">Select Goal</option>
                            {goalOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </Field>
                </TwoCol>

                <TwoCol>
                    <Field label="Location">
                        <input
                            type="text"
                            value={form.location}
                            onChange={(e) => updateField("location", e.target.value)}
                            placeholder="e.g. Ahmedabad, India"
                        />
                    </Field>

                    <Field label="Primary Audience">
                        <input
                            type="text"
                            value={form.primaryAudience}
                            onChange={(e) => updateField("primaryAudience", e.target.value)}
                            placeholder="e.g. Startups, business owners, shoppers"
                        />
                    </Field>
                </TwoCol>

                <Field label="Description" full>
                    <textarea
                        value={form.description}
                        onChange={(e) => updateField("description", e.target.value)}
                        placeholder="Describe what the business does, who it helps, and what makes it valuable."
                        rows={5}
                    />
                </Field>
            </div>

            <div className="wg-block">
                <div className="wg-block-header">
                    <div>
                        <h3 className="wg-block-title">Recommended Pages</h3>
                        <p className="wg-block-subtitle">
                            AI can refine these later based on your business and content direction.
                        </p>
                    </div>
                </div>

                <div className="wg-chip-row">
                    {(form.pages || []).map((page) => (
                        <span key={page} className="wg-chip">
                            {page}
                        </span>
                    ))}
                </div>
            </div>

            <div className="wg-block">
                <div className="wg-block-header">
                    <div>
                        <h3 className="wg-block-title">Offerings / Products / Programs</h3>
                        <p className="wg-block-subtitle">
                            Optional. Keep collapsed for faster generation, or expand to give AI a
                            stronger showcase direction.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="wg-secondary-btn"
                        onClick={() => setShowOfferings((prev) => !prev)}
                    >
                        {showOfferings ? "Hide Offerings" : "Show Offerings"}
                    </button>
                </div>

                {showOfferings && (
                    <>
                        <div className="wg-offering-topbar">
                            <button
                                type="button"
                                className="wg-secondary-btn"
                                onClick={addOfferedItem}
                            >
                                + Add Item
                            </button>
                        </div>

                        {offeredItems.map((item, itemIndex) => (
                            <div className="wg-offering-card" key={item.id || itemIndex}>
                                <div className="wg-offering-head">
                                    <h4>Item {itemIndex + 1}</h4>
                                    <button
                                        type="button"
                                        className="wg-danger-btn"
                                        onClick={() => removeOfferedItem(itemIndex)}
                                    >
                                        Remove
                                    </button>
                                </div>

                                <TwoCol>
                                    <Field label="Name">
                                        <input
                                            type="text"
                                            value={item.name || ""}
                                            onChange={(e) =>
                                                updateOfferedItem(itemIndex, "name", e.target.value)
                                            }
                                        />
                                    </Field>

                                    <Field label="Subtitle">
                                        <input
                                            type="text"
                                            value={item.subtitle || ""}
                                            onChange={(e) =>
                                                updateOfferedItem(itemIndex, "subtitle", e.target.value)
                                            }
                                        />
                                    </Field>
                                </TwoCol>

                                <TwoCol>
                                    <Field label="Badge">
                                        <input
                                            type="text"
                                            value={item.badge || ""}
                                            onChange={(e) =>
                                                updateOfferedItem(itemIndex, "badge", e.target.value)
                                            }
                                        />
                                    </Field>

                                    <Field label="Category">
                                        <input
                                            type="text"
                                            value={item.category || ""}
                                            onChange={(e) =>
                                                updateOfferedItem(itemIndex, "category", e.target.value)
                                            }
                                        />
                                    </Field>
                                </TwoCol>

                                <TwoCol>
                                    <Field label="Audience">
                                        <input
                                            type="text"
                                            value={item.audience || ""}
                                            onChange={(e) =>
                                                updateOfferedItem(itemIndex, "audience", e.target.value)
                                            }
                                        />
                                    </Field>

                                    <Field label="Level / Type">
                                        <input
                                            type="text"
                                            value={item.level || ""}
                                            onChange={(e) =>
                                                updateOfferedItem(itemIndex, "level", e.target.value)
                                            }
                                        />
                                    </Field>
                                </TwoCol>

                                <Field label="Image Preference" full>
                                    <input
                                        type="text"
                                        value={item.imagePreference || ""}
                                        onChange={(e) =>
                                            updateOfferedItem(itemIndex, "imagePreference", e.target.value)
                                        }
                                        placeholder="e.g. premium product shot, studio image, lifestyle image"
                                    />
                                </Field>

                                <Field label="Description" full>
                                    <textarea
                                        value={item.description || ""}
                                        onChange={(e) =>
                                            updateOfferedItem(itemIndex, "description", e.target.value)
                                        }
                                        rows={4}
                                    />
                                </Field>

                                <div className="wg-keypoints-head">
                                    <h5>Key Points</h5>
                                    <button
                                        type="button"
                                        className="wg-secondary-btn"
                                        onClick={() => addKeyPoint(itemIndex)}
                                    >
                                        + Add Point
                                    </button>
                                </div>

                                {(item.keyPoints || []).map((point, pointIndex) => (
                                    <div className="wg-keypoint-row" key={`${itemIndex}-${pointIndex}`}>
                                        <input
                                            type="text"
                                            value={point || ""}
                                            onChange={(e) =>
                                                updateKeyPoint(itemIndex, pointIndex, e.target.value)
                                            }
                                            placeholder={`Point ${pointIndex + 1}`}
                                        />
                                        <button
                                            type="button"
                                            className="wg-danger-btn"
                                            onClick={() => removeKeyPoint(itemIndex, pointIndex)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </>
                )}
            </div>

            {(mode === "detailed" || showAdvanced) && (
                <div className="wg-block">
                    <div className="wg-block-header">
                        <div>
                            <h3 className="wg-block-title">Advanced Options</h3>
                            <p className="wg-block-subtitle">
                                Optional controls for users who want tighter creative direction.
                            </p>
                        </div>

                        <button
                            type="button"
                            className="wg-secondary-btn"
                            onClick={() => setShowAdvanced((prev) => !prev)}
                        >
                            {showAdvanced ? "Hide Advanced" : "Show Advanced"}
                        </button>
                    </div>

                    {showAdvanced && (
                        <>
                            <TwoCol>
                                <Field label="Tone">
                                    <select
                                        value={form.tone}
                                        onChange={(e) => updateField("tone", e.target.value)}
                                    >
                                        {toneOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </Field>

                                <Field label="Secondary Audience">
                                    <input
                                        type="text"
                                        value={form.secondaryAudience}
                                        onChange={(e) => updateField("secondaryAudience", e.target.value)}
                                    />
                                </Field>
                            </TwoCol>

                            <TwoCol>
                                <Field label="Service Areas">
                                    <input
                                        type="text"
                                        value={form.serviceAreas}
                                        onChange={(e) => updateField("serviceAreas", e.target.value)}
                                    />
                                </Field>

                                <Field label="CTA Type">
                                    <select
                                        value={form.callToActionType}
                                        onChange={(e) => updateField("callToActionType", e.target.value)}
                                    >
                                        <option value="">Select CTA</option>
                                        {ctaOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                            </TwoCol>

                            <TwoCol>
                                <Field label="Brand Style">
                                    <select
                                        value={form.brandStyle}
                                        onChange={(e) => updateField("brandStyle", e.target.value)}
                                    >
                                        <option value="">Select Brand Style</option>
                                        {brandStyleOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </Field>

                                <Field label="Price Positioning">
                                    <select
                                        value={form.pricePositioning}
                                        onChange={(e) => updateField("pricePositioning", e.target.value)}
                                    >
                                        <option value="">Select Positioning</option>
                                        {pricePositioningOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                            </TwoCol>

                            <Field label="Core Services" full>
                                <textarea
                                    value={form.coreServices}
                                    onChange={(e) => updateField("coreServices", e.target.value)}
                                    rows={4}
                                />
                            </Field>

                            <Field label="Project Types / Products" full>
                                <textarea
                                    value={form.projectTypes}
                                    onChange={(e) => updateField("projectTypes", e.target.value)}
                                    rows={4}
                                />
                            </Field>

                            <Field label="Unique Selling Points" full>
                                <textarea
                                    value={form.uniqueSellingPoints}
                                    onChange={(e) => updateField("uniqueSellingPoints", e.target.value)}
                                    rows={4}
                                />
                            </Field>

                            <Field label="Trust Signals" full>
                                <textarea
                                    value={form.trustSignals}
                                    onChange={(e) => updateField("trustSignals", e.target.value)}
                                    rows={4}
                                />
                            </Field>

                            <TwoCol>
                                <Field label="Years in Business">
                                    <input
                                        type="text"
                                        value={form.yearsInBusiness}
                                        onChange={(e) => updateField("yearsInBusiness", e.target.value)}
                                    />
                                </Field>

                                <Field label="Visual Tone">
                                    <input
                                        type="text"
                                        value={form.visualTone}
                                        onChange={(e) => updateField("visualTone", e.target.value)}
                                    />
                                </Field>
                            </TwoCol>

                            <TwoCol>
                                <Field label="Must Include Words">
                                    <input
                                        type="text"
                                        value={form.mustIncludeWords}
                                        onChange={(e) => updateField("mustIncludeWords", e.target.value)}
                                    />
                                </Field>

                                <Field label="Avoid Words">
                                    <input
                                        type="text"
                                        value={form.avoidWords}
                                        onChange={(e) => updateField("avoidWords", e.target.value)}
                                    />
                                </Field>
                            </TwoCol>

                            <TwoCol>
                                <Field label="Competitor Website URL">
                                    <input
                                        type="text"
                                        value={form.competitorWebsiteUrl}
                                        onChange={(e) => updateField("competitorWebsiteUrl", e.target.value)}
                                    />
                                </Field>

                                <Field label="Reference Website Type">
                                    <input
                                        type="text"
                                        value={form.referenceWebsiteType}
                                        onChange={(e) => updateField("referenceWebsiteType", e.target.value)}
                                    />
                                </Field>
                            </TwoCol>

                            <Field label="Competitor Style Notes" full>
                                <textarea
                                    value={form.competitorStyleNotes}
                                    onChange={(e) => updateField("competitorStyleNotes", e.target.value)}
                                    rows={3}
                                />
                            </Field>

                            <TwoCol>
                                <Field label="Business Model">
                                    <input
                                        type="text"
                                        value={form.businessModel}
                                        onChange={(e) => updateField("businessModel", e.target.value)}
                                    />
                                </Field>

                                <Field label="Primary Website Mode">
                                    <input
                                        type="text"
                                        value={form.primaryWebsiteMode}
                                        onChange={(e) => updateField("primaryWebsiteMode", e.target.value)}
                                    />
                                </Field>
                            </TwoCol>

                            <TwoCol>
                                <Field label="Primary Entity Type">
                                    <input
                                        type="text"
                                        value={form.primaryEntityType}
                                        onChange={(e) => updateField("primaryEntityType", e.target.value)}
                                    />
                                </Field>

                                <Field label="Location Focus">
                                    <input
                                        type="text"
                                        value={form.locationFocus}
                                        onChange={(e) => updateField("locationFocus", e.target.value)}
                                    />
                                </Field>
                            </TwoCol>

                            <TwoCol>
                                <Field label="Desired Layout Style">
                                    <input
                                        type="text"
                                        value={form.desiredLayoutStyle}
                                        onChange={(e) => updateField("desiredLayoutStyle", e.target.value)}
                                    />
                                </Field>

                                <Field label="Content Style Hint">
                                    <input
                                        type="text"
                                        value={form.contentStyleHint}
                                        onChange={(e) => updateField("contentStyleHint", e.target.value)}
                                    />
                                </Field>
                            </TwoCol>

                            <Field label="Preferred Section Density" full>
                                <input
                                    type="text"
                                    value={form.preferredSectionDensity}
                                    onChange={(e) => updateField("preferredSectionDensity", e.target.value)}
                                />
                            </Field>
                        </>
                    )}

                    {mode === "fast" && !showAdvanced && (
                        <div className="wg-advanced-compact">
                            <button
                                type="button"
                                className="wg-secondary-btn"
                                onClick={() => setShowAdvanced(true)}
                            >
                                Show Advanced
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}